import { DataTexture, FloatType, NearestFilter, RGFormat, Vector4 } from 'three';
import { residualShiftPx, verticalShiftPx, type PainterResidualWarp } from './painterWarp';
import type { Vec2 } from '../geo/types';

const STEP_PX = 2;

interface LookupData {
  readonly key: string;
  readonly data: Float32Array;
  readonly width: number;
  readonly height: number;
  readonly origin: Vec2;
}

/* The world and both citizen previews use the same calibration. Retain one immutable CPU table;
   each factory still owns and disposes its own GPU texture. */
let cached: LookupData | undefined;

function buildLookup(warp: PainterResidualWarp, principal: Vec2, key: string): LookupData {
  const row = warp.vertical?.lastFittedRowPx ?? 0;
  /* Align the clamped vertical field's last fitted row with a texel. Bilinear interpolation
     must not smooth that intentional change of slope across neighbouring rows. */
  const origin: Vec2 = [-principal[0], row - Math.ceil((row + principal[1]) / STEP_PX) * STEP_PX];
  const width = Math.ceil((principal[0] * 4) / STEP_PX) + 1;
  const height = Math.ceil((principal[1] * 3 - origin[1]) / STEP_PX) + 1;
  const data = new Float32Array(width * height * 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pixel: Vec2 = [origin[0] + x * STEP_PX, origin[1] + y * STEP_PX];
      const at = (y * width + x) * 2;
      data[at] = residualShiftPx(warp, pixel);
      data[at + 1] = verticalShiftPx(warp, pixel);
    }
  }
  return { key, data, width, height, origin };
}

/** A subpixel-accurate shift table; the shader retains the fitted splines outside its domain. */
export function painterWarpLookup(warp: PainterResidualWarp, principal: Vec2) {
  const key = JSON.stringify([warp, principal]);
  if (cached?.key !== key) cached = buildLookup(warp, principal, key);
  const texture = new DataTexture(cached.data, cached.width, cached.height, RGFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.needsUpdate = true;
  return {
    texture,
    grid: new Vector4(cached.origin[0], cached.origin[1], 1 / STEP_PX, 1 / STEP_PX),
  };
}
